import { useCallback, useEffect, useState } from "react";
import { getBrandSearch } from "~src/api/search";
import { Brand } from "~src/types/brand";
import * as Sentry from "@sentry/react";

type FetchProps = {
  page?: number;
  page_size?: number;
  search_text?: string;
};

export type BrandSearchContext = {
  data: Brand[];
  loadMore: () => void;
  loading: boolean;
  total: number | null;
  error?: boolean;
};

export const useInfiniteBrandSearch = (
  params: FetchProps,
): BrandSearchContext => {
  const [data, setData] = useState<Brand[]>([]);
  const [total, setTotal] = useState<null | number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  const loadMore = useCallback(async () => {
    if (hasMore && !loading && !initialLoad) {
      setLoading(true);
      try {
        const response = await getBrandSearch({
          ...params,
          page: currentPage + 1,
        });
        if (response.results.length > 0) {
          setData((prevItems) => [...prevItems, ...response.results]);
          setCurrentPage((prevPage) => prevPage + 1);
          setHasMore(!!response.next);
        } else {
          setHasMore(false);
        }
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useInfiniteBrandSearch");
          scope.setExtra("operation", "loadMore");
          Sentry.captureException(err);
        });
        setError(true);
      } finally {
        setLoading(false);
      }
    }
  }, [currentPage, hasMore, loading, initialLoad, params]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setData([]);
      setTotal(null);
      setInitialLoad(true);
      setCurrentPage(1);
      setLoading(true);
      try {
        const response = await getBrandSearch({
          ...params,
          page: currentPage,
        });
        setData(response.results);
        setHasMore(!!response.next);
        setTotal(response.count);
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useInfiniteBrandSearch");
          scope.setExtra("operation", "fetchData");
          Sentry.captureException(err);
        });
        setError(true);
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search_text, params.page_size]);

  return { data, loadMore, loading, error, total };
};
